import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import type {
    GenerateActInputDto,
    GetActsInputDto,
    UpdateActNumberInputDto,
    UploadFinalActInputDto,
    ActUnion,
} from '@synapex/shared2/gateway-rest-api'
import {
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from '@tanstack/react-query'

import { DocumentRecord } from '@synapex/prisma/client'

import { apiQueryKey } from '../../'
import { OmitWwld } from '../../utils'
import {
    apiDeleteAct,
    apiGenerateAct,
    apiGetActs,
    apiUpdateActNumber,
    apiUploadActFile,
} from '../api'

export const useGetActs = (
    input: GetActsInputDto,
    options?: Omit<UseQueryOptions<ActUnion[], Error>, 'queryKey'>
) => {
    const { contragentDistributorId, ...wd } = input
    return useQuery<ActUnion[], Error>({
        queryKey: apiQueryKey.acts(wd, contragentDistributorId),
        queryFn: () => apiGetActs(input),
        ...options,
    })
}

export const useGenerateAct = (wwld: WorkspaceWithLinkDescriptor) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['generate-act'],
        mutationFn: (input: OmitWwld<GenerateActInputDto>) =>
            apiGenerateAct({ ...wwld, ...input }),
        onSuccess: (_, { ordContragentId, contractId, customContractId }) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.acts(wwld, ordContragentId),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(),
                predicate: (query) => {
                    if (!query.state.data) {
                        return false
                    }
                    const queryData = query.state.data as {
                        draft_contract_id?: number
                        custom_contract_id?: number
                    }
                    return (
                        queryData.draft_contract_id === contractId ||
                        queryData.custom_contract_id === customContractId
                    )
                },
            })
        },
    })
}

export const useDeleteAct = (wwld: WorkspaceWithLinkDescriptor) => {
    const queryClient = useQueryClient()

    return useMutation<
        { message: string },
        Error,
        {
            ordContragentId: number
            documentRecordId: number
        }
    >({
        mutationKey: ['delete-act'],
        mutationFn: ({ documentRecordId }) => apiDeleteAct(documentRecordId),
        onSuccess: (_, { ordContragentId, documentRecordId }) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.acts(wwld, ordContragentId),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(),
                predicate: (query) => {
                    if (!query.state.data) {
                        return false
                    }
                    const queryData = query.state.data as {
                        draft_act_id?: number
                    }
                    return queryData.draft_act_id === documentRecordId
                },
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.vkOrdHistory(wwld),
            })
        },
    })
}

export const useUploadFinalActFile = (
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<
        UseMutationOptions<
            void,
            Error,
            OmitWwld<UploadFinalActInputDto> & { file: File }
        >,
        'mutationFn'
    >
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['upload-file'],
        mutationFn: (
            input: OmitWwld<UploadFinalActInputDto> & { file: File }
        ) => apiUploadActFile({ ...wwld, ...input }) as Promise<void>,

        onSuccess(_, { ordContragentId, draftActId }) {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.acts(wwld, ordContragentId),
            })

            // Invalidate only marking forms that have matching draftActId
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(),
                predicate: (query) => {
                    if (!query.state.data) {
                        return false
                    }
                    const queryData = query.state.data as {
                        draft_act_id?: number
                    }
                    return queryData.draft_act_id === draftActId
                },
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.vkOrdHistory(wwld),
            })
        },
        ...options,
    })
}

export const useUpdateActNumber = (
    wwld: WorkspaceWithLinkDescriptor,
    options?: Omit<
        UseMutationOptions<DocumentRecord, Error, UpdateActNumberInputDto>,
        'mutationFn'
    >
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationKey: ['update-act-number'],
        mutationFn: (input: OmitWwld<UpdateActNumberInputDto>) =>
            apiUpdateActNumber({
                ...wwld,
                ...input,
            }) as Promise<DocumentRecord>,
        onSuccess: (_, input) => {
            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.acts(wwld, input.ordContragentId),
            })

            void queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
        },
        ...options,
    })
}
